import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useContent } from "./ContentContext";
import { mapToCanonicalPracticeArea } from "../data/practiceAttorneyMap";
import PracticeAreaDialog from "../components/PracticeAreaDialog";
import AttorneyBioDialog from "../components/AttorneyBioDialog";

const MODAL_TRANSITION_MS = 150;

const ModalsContext = createContext({
  openPracticeArea: () => {},
  openAttorneyBio: () => {},
});

export function useModals() {
  return useContext(ModalsContext);
}

export function usePracticeAreaModal() {
  const { openPracticeArea } = useContext(ModalsContext);
  return { openPracticeArea };
}

export function useAttorneyBioModal() {
  const { openAttorneyBio } = useContext(ModalsContext);
  return { openAttorneyBio };
}

export function ModalsProvider({ children }) {
  const { practiceAreas, attorneys } = useContent();
  const [activePractice, setActivePractice] = useState(null);
  const [activeAttorney, setActiveAttorney] = useState(null);

  const openPracticeArea = useCallback((idOrTitle) => {
    const slug = idOrTitle.toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
    const area = practiceAreas.find((a) => a.id === idOrTitle || a.title === idOrTitle || a.id === slug || (a.previousIds || []).includes(slug));
    if (area) setActivePractice(area);
  }, [practiceAreas]);

  const openAttorneyBio = useCallback((idOrAttorney) => {
    const a = typeof idOrAttorney === "string" ? attorneys.find((x) => x.id === idOrAttorney) : idOrAttorney;
    if (a) setActiveAttorney(a);
  }, [attorneys]);

  const closePractice = useCallback(() => setActivePractice(null), []);
  const closeAttorney = useCallback(() => setActiveAttorney(null), []);

  const handlePracticeAttorneyClick = useCallback((attorneyId) => {
    setActivePractice(null);
    setTimeout(() => openAttorneyBio(attorneyId), MODAL_TRANSITION_MS);
  }, [openAttorneyBio]);

  const handleBioPracticeClick = useCallback((rawTitle) => {
    const canonical = mapToCanonicalPracticeArea(rawTitle);
    if (!canonical) return;
    setActiveAttorney(null);
    setTimeout(() => openPracticeArea(canonical), MODAL_TRANSITION_MS);
  }, [openPracticeArea]);

  const value = useMemo(() => ({ openPracticeArea, openAttorneyBio }), [openPracticeArea, openAttorneyBio]);

  return (
    <ModalsContext.Provider value={value}>
      {children}
      <PracticeAreaDialog
        area={activePractice}
        open={!!activePractice}
        onClose={closePractice}
        onAttorneyClick={handlePracticeAttorneyClick}
      />
      <AttorneyBioDialog
        attorney={activeAttorney}
        open={!!activeAttorney}
        onClose={closeAttorney}
        onPracticeClick={handleBioPracticeClick}
      />
    </ModalsContext.Provider>
  );
}
